namespace Ir.Components.User.UserGroupAccessEditor {
    interface IViewModel {
        gridOptions: DevExpress.ui.dxDataGrid.Properties;
        gridComponent: DevExpress.ui.dxDataGrid;

        access: Array<UserAccess>;
        fields: Array<FieldAccessVM>;
        organizations: Array<UserGroupOrganizationVM>;
        getUserProfile: () => number

        userProfileId: number;
        userProfileCreateWorkbooks: boolean;
    }
    interface IParams {
        referenceId: number;
        controlType: AccessControlTypeEnum;
        getUserProfile: () => number;
    }

    export function getViewModel(this: IViewModel, params: IParams) {
        let self = this;
        load(self, params);

        self.gridOptions = {
            height: "100%",
            width: "100%",
            paging: {
                enabled: false
            },
            scrolling: {
                useNative: false
            },
            dataSource: self.access,
            columns: [
                {
                    dataField: "owner.name",
                    caption: params.controlType === AccessControlTypeEnum.User ? "User" : params.controlType === AccessControlTypeEnum.Group ? "Group" : "Mapping",
                    allowEditing: false,
                    width: params.controlType === AccessControlTypeEnum.User ? "30%" : "40%",
                    headerFilter: {
                        search: {
                            enabled: true
                        },
                        width: 400
                    }
                },
                {
                    dataField: "owner.loginName",
                    caption: "Login",
                    allowEditing: false,
                    visible: params.controlType === AccessControlTypeEnum.User,
                    width: "15%",
                    headerFilter: {
                        search: {
                            enabled: true
                        },
                        width: 400
                    }
                },
                {
                    dataField: "owner.organizationId",
                    caption: "Organization",
                    width: "25%",
                    visible: irSettings.multiTenant && params.controlType === AccessControlTypeEnum.User,
                    lookup: {
                        dataSource: self.organizations,
                        displayExpr: 'name',
                        valueExpr: 'id',
                    },
                    allowEditing: false,
                    headerFilter: {
                        search: {
                            enabled: true
                        },
                        width: 400
                    }
                },
                {
                    dataField: "selected",
                    caption: "Access",
                    width: 100,
                    headerFilter: { dataSource: [{ value: ["selected", "=", false], text: "No Access" }, { value: ["selected", "=", true], text: "Access" }] },
                    setCellValue(rowData, value, currentRowData: UserAccess) {
                        this.defaultSetCellValue(rowData, value, currentRowData);
                        rowData.type = rowData.selected ? AccessTypeEnum.Full : AccessTypeEnum.None;
                        rowData.fieldId = null;
                        rowData.value = null;

                        if (value) {
                            if (params.controlType === AccessControlTypeEnum.MappingGroup || params.controlType === AccessControlTypeEnum.Group) {
                                rowData.createWorkbooks = true;
                            }
                            else {
                                getUserProfilePermission(self, params, currentRowData);

                                rowData.createWorkbooks = currentRowData.owner.userProfileCreateWorkbooks;
                            }
                        }
                        else {
                            rowData.createWorkbooks = false;
                        }
                    } 
                },
                {
                    dataField: "createWorkbooks",
                    caption: "Create Workbooks",
                    width: 150,
                    setCellValue(rowData, value, currentRowData) {
                        this.defaultSetCellValue(rowData, value, currentRowData);
                    }
                },
                {
                    dataField: "type",
                    caption: "Access Type",
                    width: 130,
                    headerFilter: { dataSource: getAccessTypes(true) },
                    lookup: {
                        dataSource: getAccessTypes(true),
                        displayExpr: 'text',
                        valueExpr: 'value'
                    },
                    setCellValue(rowData, value, currentRowData) {
                        this.defaultSetCellValue(rowData, value, currentRowData);

                        if (rowData.type === AccessTypeEnum.Full || rowData.type === AccessTypeEnum.None) {
                            rowData.fieldId = null;
                            rowData.value = null;
                        }
                    },
                    validationRules: [{ type: 'required' }]
                },
                {
                    dataField: "fieldId",
                    caption: "Field",
                    width: "25%",
                    lookup: {
                        dataSource: self.fields,
                        displayExpr(e: FieldAccessVM) {
                            return replaceBasicHtmlContent(e?.name);
                        },
                        valueExpr: 'id',
                    },
                    setCellValue(rowData, value, currentRowData) {
                        this.defaultSetCellValue(rowData, value, currentRowData);
                        rowData.value = null;
                    },
                    allowHeaderFiltering: false,
                    validationRules: [{
                        type: "custom",
                        validationCallback: function (v) {
                            if (v.data.type === AccessTypeEnum.Full || v.data.type === AccessTypeEnum.None || !v.data.selected) {
                                return true;
                            }

                            if (v.value === undefined || v.value === null || v.value === 0) {
                                return false;
                            }

                            return true;
                        },
                        message: "Field is required"
                    }]
                },
                {
                    dataField: "value",
                    caption: "Restrict/Default Value",
                    width: "35%",
                    editorOptions: {
                        hint: "Use a comma as a separator for more than one value. For dates, use yyyy-mm-dd as a mask and for date ranges, use a comma to separate the start and end dates."
                    },
                    allowHeaderFiltering: false,
                    validationRules: [{
                        type: "custom",
                        validationCallback: function (v) {
                            if (v.data.type === AccessTypeEnum.Full || v.data.type === AccessTypeEnum.None || !v.data.selected) {
                                return true;
                            }

                            if (v.value === undefined || v.value === null) {
                                return false;
                            }

                            if (v.value.trim() === "") {
                                return false;
                            }

                            return true;
                        },
                        message: "Restrict/Default Value is required"
                    }]
                },
            ],
            editing: {
                mode: 'batch',
                allowUpdating: true,
                startEditAction: 'click',
            },
            showBorders: true,
            repaintChangesOnly: true,
            keyExpr: "owner.id",
            headerFilter: {
                visible: true
            },
            searchPanel: {
                visible: true,
                highlightSearchText: true,
            },
            toolbar: {
                items: [
                    {
                        name: "saveButton", cssClass: "customButtonDataGridAdmin saveButton", location: "before",
                        widget: 'dxButton',
                        visible: params.controlType === AccessControlTypeEnum.User || params.controlType === AccessControlTypeEnum.Group,
                        options: {
                            text: 'Save',
                        }
                    }, {
                        name: "revertButton", cssClass: "customButtonDataGridAdmin cancelButton", location: "before",
                        widget: 'dxButton',
                        visible: params.controlType === AccessControlTypeEnum.User || params.controlType === AccessControlTypeEnum.Group,
                        options: {
                            text: 'Cancel',
                            icon: 'close'
                        }
                    }, {
                        cssClass: "customButtonDataGridAdmin", location: "before",
                        widget: 'dxButton',
                        options: {
                            text: 'Grant to all',
                            icon: 'selectall',
                            onClick: function () {
                                self.gridComponent.beginUpdate();
                                let changes = self.gridComponent.option('editing.changes') as any;
                                self.gridComponent.getDataSource().items().forEach(function (row: UserAccess) {
                                    let changed = changes?.find(x => x.key === row.owner.id)
                                    if (!row.selected || (changed && !changed.data.selected)) {
                                        let rowIndex = self.gridComponent.getRowIndexByKey(row.owner.id);
                                        self.gridComponent.cellValue(rowIndex, "selected", true);
                                    }
                                });
                                self.gridComponent.endUpdate();
                            }
                        }
                    }, {
                        cssClass: "customButtonDataGridAdmin", location: "before",
                        widget: 'dxButton',
                        options: {
                            text: 'Revoke to all',
                            icon: 'unselectall',
                            onClick: function () {
                                self.gridComponent.beginUpdate();
                                let changes = self.gridComponent.option('editing.changes') as any;
                                self.gridComponent.getDataSource().items().forEach(function (row: UserAccess) {
                                    let changed = changes?.find(x => x.key === row.owner.id)
                                    if (row.selected || (changed && changed.data.selected)) {
                                        let rowIndex = self.gridComponent.getRowIndexByKey(row.owner.id);
                                        self.gridComponent.cellValue(rowIndex, "selected", false);
                                    }
                                });
                                self.gridComponent.endUpdate();
                            }
                        }
                    }, {
                        name: "searchPanel",
                        location: "after"
                    },
                ]
            },
            onInitialized(e) {
                self.gridComponent = e.component;
            },
            onEditorPreparing(e) {
                if (e.parentType === "dataRow") {
                    let access = e.row.data as UserAccess;
                    if (e.dataField === "value") {
                        e.editorOptions.disabled = !access.selected || access.fieldId === 0 || access.fieldId === null || access.type === AccessTypeEnum.Full || access.type === AccessTypeEnum.None;
                    }
                    else if (e.dataField === "fieldId") {
                        e.editorOptions.disabled = !access.selected || access.type === AccessTypeEnum.Full || access.type === AccessTypeEnum.None;

                        if (params.controlType === AccessControlTypeEnum.MappingUser || params.controlType === AccessControlTypeEnum.MappingGroup) {
                            e.editorOptions.dataSource = access.fields;
                        }
                    }
                    else if (e.dataField === "type") {
                        e.editorOptions.disabled = !access.selected;
                        e.editorOptions.dataSource = getAccessTypes(!access.selected);
                    }
                    else if (e.dataField === "createWorkbooks") {
                        e.editorOptions.disabled = !access.selected;
                    }
                }
            },
            onSaved(e) {
                if (e.changes && (params.controlType === AccessControlTypeEnum.User || params.controlType === AccessControlTypeEnum.Group)) {
                    let model = {
                        usersAccess: [],
                        mappingId: params.referenceId
                    };
                    e.changes.forEach(function (item) {
                        let userAccess = item.data as UserAccess;
                        model.usersAccess.push({
                            id: userAccess.id,
                            selected: userAccess.selected,
                            type: userAccess.type,
                            fieldId: userAccess.fieldId,
                            value: userAccess.value,
                            ownerId: userAccess.owner.id,
                            createWorkbooks: userAccess.createWorkbooks
                        });
                    })

                    let isUserAccess = params.controlType === AccessControlTypeEnum.User;
                    irGlobal.updateFromServer({},
                        isUserAccess ? urlConfigs.mapping + "/SaveUserAccess" : urlConfigs.mapping + "/SaveGroupAccess",
                        model,
                        function (data) {
                            if (!data.success) {
                                DevExpress.ui.dialog.alert(data.message, isUserAccess ? "User Access" : "Group Access");
                                location.reload();
                            }
                        }.bind(this),
                        function (textStatus, errorThrown, errorItem: IError) {
                            errorItem.userMessage = `Failed to save ${(isUserAccess ? "user access" : "group access")}`;
                            irGlobal.defaultErrorFunction(errorItem.userMessage, textStatus, errorThrown);
                        },
                        true,
                        "POST",
                        true);
                }
            },
            onCellPrepared(e) {
                if (e.data && e.value !== undefined && !(e.data as UserAccess).owner.active) {
                    if (e.columnIndex === 5 && e.value === AccessTypeEnum.None) {
                        return;
                    }

                    e.cellElement.addClass("ir-inactive");
                }
            }
        };
    }

    function load(self: IViewModel, params: IParams) {
        let url = "";
        let userMessage = "";
        let data = { userId: params.referenceId } as any;
        if (params.controlType === AccessControlTypeEnum.User || params.controlType === AccessControlTypeEnum.Group) {
            data = { mappingId: params.referenceId };
            url = params.controlType === AccessControlTypeEnum.User ? urlConfigs.mapping + "/GetUserAccess" : urlConfigs.mapping + "/GetGroupAccess";
            userMessage = `Failed to get ${(params.controlType === AccessControlTypeEnum.User ? "user" : "group")} accesses`;
        }
        else if (params.controlType === AccessControlTypeEnum.MappingUser) {
            url = urlConfigs.user + "/GetUserAccessByMapping";
            userMessage = "Failed to get user accesses";
        }
        else if (params.controlType === AccessControlTypeEnum.MappingGroup) {
            url = urlConfigs.group + "/GetGroupAccess";
            userMessage = "Failed to get group accesses";
        }
        
        let o = {
            url: url,
            data: data,
            dataType: "json",
            async: false
        } as RequestServerParams;
        o.successFunction = function (result: any, e) {
            self.fields = createInstances(FieldAccessVM, result?.fields ?? []);
            self.organizations = createInstances(UserGroupOrganizationVM, result?.organizations ?? []);
            self.access = [];

            let accessList = [];
            if (params.controlType === AccessControlTypeEnum.User) {
                accessList = result.userAccess;
            }
            else if (params.controlType === AccessControlTypeEnum.Group) {
                accessList = result.groupAccess;
            }
            else if (params.controlType === AccessControlTypeEnum.MappingUser || params.controlType === AccessControlTypeEnum.MappingGroup) {
                accessList = result.mappingAccess;
            }

            accessList.forEach(function (tmp) {
                let newTemplate = new UserAccess(tmp);
                self.access.push(newTemplate);

                if (params.controlType === AccessControlTypeEnum.MappingUser || params.controlType === AccessControlTypeEnum.MappingGroup) {
                    self.fields = self.fields.concat(newTemplate.fields);
                }
            });
        };
        o.failureFunction = function (textStatus, errorThrown, errorItem: IError) {
            errorItem.userMessage = userMessage;
            irGlobal.defaultErrorFunction(errorItem.userMessage, textStatus, errorThrown);
        };

        irGlobal.requestServer(o);
    }

    function getAccessTypes(includeNoneOption: boolean = false) {
        let types = [
            { value: AccessTypeEnum.Full, text: "Full Access" },
            { value: AccessTypeEnum.Restrict, text: "Restrict" },
            { value: AccessTypeEnum.Default, text: "Default" },
        ];

        if (includeNoneOption) {
            types.push({ value: AccessTypeEnum.None, text: " " });
        }

        return types;
    }

    function getUserProfilePermission(self: IViewModel, params: IParams, currentRowData: UserAccess) {
        if (params.controlType === AccessControlTypeEnum.MappingUser && params.getUserProfile) {
            let userProfileId = params.getUserProfile();
            if (userProfileId === self.userProfileId) {
                currentRowData.owner.userProfileCreateWorkbooks = self.userProfileCreateWorkbooks;
                return;
            }

            self.userProfileId = userProfileId;
            let o = {
                url: urlConfigs.user + "/GetUserProfilePermissions",
                data: {
                    userProfileId: self.userProfileId
                },
                async: false,
            } as RequestServerParams;
            o.successFunction = function (data, e) {
                self.userProfileCreateWorkbooks = data.createWorkbooks;
                currentRowData.owner.userProfileCreateWorkbooks = data.createWorkbooks;
            };

            irGlobal.requestServerAdmin(o);
        }
    }

    export function save(element: JQuery<Element>, callback: Function) {
        const grid = $("#userGroupAccessGrid", element).data("dxDataGrid");
        if (grid) {
            if (grid.hasEditData()) {
                grid.saveEditData().done(function () {
                    let rows = (grid.getDataSource().store() as any)._array
                    callback(rows);
                });
            }
            else {
                let rows = (grid.getDataSource().store() as any)._array
                callback(rows);
            }
        }
        else {
            callback(null);
        }
    }

    export function validate(element: JQuery<Element>, callback: Function) {
        const grid = $("#userGroupAccessGrid", element).data("dxDataGrid") as any;
        if (grid) {
            const currentChanges = grid.option('editing.changes') as any;
            const fakeChanges = grid.getVisibleRows().map((row) => ({ type: 'update', key: row.key, data: {} }));

            grid.option('editing.changes', [...currentChanges, ...fakeChanges]);
            grid.repaint();

            grid.getController('validating').validate(true).then((result) => {
                return callback(result);
            });
        }
        else {
            callback(true);
        }
    }
}